// TrailBivvy Pro on this device: the web counterpart of the iOS EntitlementManager (Sources/Store/EntitlementManager.swift).
// Keeps the signed token in IndexedDB, decides access offline with rules.ts, refreshes from the Worker when online,
// and runs the purchase, restore and transfer flows (decisions.md PWA-12, PWA-16).

import { signal, type Signal } from '@preact/signals';
import type { KeyValueStore } from '../storage/idb';
import { ApiError, type EntitlementAnswer, type SubscriptionApi } from './api';
import { accessFromClaims, lockedStatus, factsFromClaims, type SubscriptionStatus } from './rules';
import { verifyToken, type EntitlementClaims } from './token';

/** Same three states as iOS `EntitlementManager.Access`. */
export type Access = 'entitled' | 'checking' | 'locked';

export interface SubscriptionDetails {
  status: SubscriptionStatus;
  isFreeTrial: boolean;
  /** End of the current trial or paid period (epoch ms). */
  periodEndsAt: number;
  willAutoRenew: boolean;
  /** Last instant with access (epoch ms), when entitled. */
  accessUntil?: number;
}

export interface StoredEntitlement {
  token: string;
  /** When the Worker last confirmed it (epoch ms). */
  refreshedAt: number;
}

export interface EntitlementDeps {
  store: KeyValueStore<StoredEntitlement>;
  api: SubscriptionApi;
  trustedKeys: Readonly<Record<string, JsonWebKey>>;
  trustedIssuers: () => string[];
  now?: () => number;
  isOnline?: () => boolean;
}

const STORE_KEY = 'pro';
/** Refresh at most this often while things are fine (iOS re-reads StoreKit on every return to the foreground). */
const refreshInterval = 12 * 3_600_000;
/** setTimeout cannot wait longer than ~24.8 days. */
const maxTimer = 2 ** 31 - 1;

export type FlowResult = { ok: true } | { ok: false; code: string };

export class EntitlementManager {
  readonly access: Signal<Access> = signal('checking');
  readonly details: Signal<SubscriptionDetails | undefined> = signal(undefined);
  readonly lastConfirmedAt: Signal<number | undefined> = signal(undefined);
  /** A refresh is running (for a spinner on "Restore"). */
  readonly busy: Signal<boolean> = signal(false);

  private claims: EntitlementClaims | undefined;
  private token: string | undefined;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private readonly now: () => number;
  private readonly isOnline: () => boolean;

  constructor(private readonly deps: EntitlementDeps) {
    this.now = deps.now ?? (() => Date.now());
    this.isOnline = deps.isOnline ?? (() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  }

  /** Loads the stored token (no paywall flash for subscribers), then refreshes it in the background when due. */
  async start(): Promise<void> {
    let stored: StoredEntitlement | undefined;
    try {
      stored = await this.deps.store.get(STORE_KEY);
    } catch {
      stored = undefined; // storage unavailable (private mode): behaves as a new device
    }
    if (stored && (await this.adopt(stored.token, stored.refreshedAt, false))) {
      if (this.now() - stored.refreshedAt > refreshInterval) void this.refresh();
    } else {
      this.setLocked(undefined);
    }
  }

  /** Called when the app becomes visible again or the connection returns. */
  onResume(): void {
    this.evaluate();
    const confirmed = this.lastConfirmedAt.value;
    if (this.token && (confirmed === undefined || this.now() - confirmed > refreshInterval)) void this.refresh();
  }

  /** Asks the Worker for the current state of the subscription behind the stored token. */
  async refresh(): Promise<FlowResult> {
    if (!this.token) return { ok: false, code: 'no_token' };
    if (!this.isOnline()) return { ok: false, code: 'network' };
    return this.run(() => this.deps.api.refresh(this.token!));
  }

  /** After Stripe Checkout redirected back with its session id. */
  completeCheckout(sessionId: string): Promise<FlowResult> {
    return this.run(() => this.deps.api.completeCheckout(sessionId));
  }

  redeemRestoreCode(code: string): Promise<FlowResult> {
    return this.run(() => this.deps.api.redeemRestoreCode(code));
  }

  redeemTransferCode(code: string): Promise<FlowResult> {
    return this.run(() => this.deps.api.redeemTransferCode(code));
  }

  async createTransferCode(): Promise<{ code: string; expiresAt: number } | { error: string }> {
    if (!this.token) return { error: 'no_token' };
    try {
      return await this.deps.api.createTransferCode(this.token);
    } catch (error) {
      return { error: error instanceof ApiError ? error.code : 'unknown' };
    }
  }

  /** URL of the Stripe customer portal for this subscription. */
  async portalUrl(locale: string): Promise<{ url: string } | { error: string }> {
    if (!this.token) return { error: 'no_token' };
    try {
      return await this.deps.api.portal(this.token, locale);
    } catch (error) {
      return { error: error instanceof ApiError ? error.code : 'unknown' };
    }
  }

  /** Forgets the subscription on this device only (the subscription itself is untouched). */
  async removeFromDevice(): Promise<void> {
    this.token = undefined;
    this.claims = undefined;
    this.lastConfirmedAt.value = undefined;
    await this.deps.store.delete(STORE_KEY).catch(() => undefined);
    this.setLocked(undefined);
  }

  /** Re-decides access from the stored claims (also scheduled for the instant access ends). */
  evaluate(): void {
    if (this.timer) clearTimeout(this.timer);
    const claims = this.claims;
    if (!claims) {
      this.setLocked(undefined);
      return;
    }
    const nowSeconds = this.now() / 1000;
    const access = accessFromClaims(claims, nowSeconds);
    const facts = factsFromClaims(claims);
    const base = {
      isFreeTrial: claims.tr,
      periodEndsAt: claims.pe * 1000,
      willAutoRenew: claims.wr,
    };
    if (access) {
      this.details.value = { ...base, status: access.status, accessUntil: access.until * 1000 };
      this.access.value = 'entitled';
      const wait = Math.min(access.until * 1000 - this.now() + 1000, maxTimer);
      this.timer = setTimeout(() => this.evaluate(), Math.max(1000, wait));
    } else {
      this.setLocked({ ...base, status: lockedStatus(facts) });
    }
  }

  private setLocked(details: SubscriptionDetails | undefined) {
    this.details.value = details;
    this.access.value = 'locked';
  }

  private async run(request: () => Promise<EntitlementAnswer>): Promise<FlowResult> {
    this.busy.value = true;
    try {
      const answer = await request();
      if (!answer.token) {
        // The customer never subscribed to the plan (or the subscription is gone).
        if (answer.status === 'none') await this.removeFromDevice();
        return { ok: false, code: 'none' };
      }
      const adopted = await this.adopt(answer.token, this.now(), true);
      return adopted ? { ok: true } : { ok: false, code: 'invalid_token' };
    } catch (error) {
      return { ok: false, code: error instanceof ApiError ? error.code : 'unknown' };
    } finally {
      this.busy.value = false;
    }
  }

  /** Verifies and stores a token; false (and nothing changed) when the signature or issuer is wrong. */
  private async adopt(token: string, confirmedAt: number, persist: boolean): Promise<boolean> {
    const result = await verifyToken(token, this.deps.trustedKeys, this.deps.trustedIssuers());
    if (!result.ok) return false;
    this.token = token;
    this.claims = result.claims;
    this.lastConfirmedAt.value = confirmedAt;
    if (persist) await this.deps.store.set(STORE_KEY, { token, refreshedAt: confirmedAt }).catch(() => undefined);
    this.evaluate();
    return true;
  }
}
